import React, { useCallback, useEffect, useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { GREY } from '~/configs/colours';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { getCountryNames } from '@luxuryescapes/lib-countries';
import { categories as propertyCategories } from '@luxuryescapes/lib-global';

import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';
import Image from '~/components/Common/Image';

import { ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import { type PatchPropertyPayload, type PropertyResponse, patchProperty } from '~/services/AccommodationService';

const ACCOMM_V2_BOOKABLE_EDIT_ROLES = [ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR];
const ACCOMM_V2_BOOKABLE_LABEL = 'New Booking Flow Enabled';

interface PropertyMetadataFormData {
  name: string;
  chainName: string;
  category: PatchPropertyPayload['category'];
  accommV2Bookable: boolean;
  country: string | null;
  city: string;
}

interface Props {
  variant?: 'classic' | 'sidebar';
}

const getCategoryDefault = (category: PropertyResponse['category']): PatchPropertyPayload['category'] => {
  return propertyCategories.allCategories.includes(category as NonNullable<PatchPropertyPayload['category']>)
    ? (category as PatchPropertyPayload['category'])
    : null;
};

const getFormDefaults = (propertyDetails: PropertyResponse): PropertyMetadataFormData => ({
  name: propertyDetails.name || '',
  chainName: propertyDetails.chainName || '',
  category: getCategoryDefault(propertyDetails.category),
  accommV2Bookable: !!propertyDetails.accommV2Bookable,
  country: propertyDetails.country || null,
  city: propertyDetails.city || '',
});

const toPatchPayload = (
  data: PropertyMetadataFormData,
  { includeAccommV2Bookable }: { includeAccommV2Bookable: boolean },
): PatchPropertyPayload => ({
  name: data.name.trim(),
  chainName: data.chainName.trim() || null,
  category: data.category || null,
  ...(includeAccommV2Bookable ? { accommV2Bookable: data.accommV2Bookable } : {}),
  country: data.country?.trim() || null,
  city: data.city.trim() || null,
});

function LogoThumbnail({ logoId }: { logoId?: string | null }) {
  if (!logoId) {
    return null;
  }

  return (
    <Image
      publicId={logoId}
      options={{ width: 64, height: 64 }}
      alt="Property logo"
      style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
    />
  );
}

function SidebarReadOnlyField({
  label,
  value,
  onCopy,
  startAdornment,
}: {
  label: string;
  value: string;
  onCopy?: () => void;
  startAdornment?: React.ReactNode;
}) {
  return (
    <Box sx={{ borderBottom: 1, borderColor: GREY[200], pb: 1 }}>
      <Typography variant="body2" color="text.secondary" mb={0.75}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        {startAdornment}
        <Typography variant="body1" sx={{ overflowWrap: 'anywhere', flex: 1 }}>
          {value}
        </Typography>
        {onCopy && (
          <IconButton aria-label={`Copy ${label}`} onClick={onCopy} size="small">
            <ContentCopyOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

function SidebarChipListField({ label, values }: { label: string; values: Array<string> }) {
  return (
    <Box sx={{ borderBottom: 1, borderColor: GREY[200], pb: 1 }}>
      <Typography variant="body2" color="text.secondary" mb={0.75}>
        {label}
      </Typography>
      {values.length ? (
        <Stack direction="row" spacing={1} rowGap={1} flexWrap="wrap">
          {values.map((value) => (
            <Chip key={value} label={value} size="small" />
          ))}
        </Stack>
      ) : (
        <Typography variant="body1">-</Typography>
      )}
    </Box>
  );
}

export default function AccommodationPropertyDetailsOverviewPane({ variant = 'classic' }: Props) {
  const { propertyDetails, setPropertyDetails, refreshPropertyDetails } = useAccommodationPropertyDetailsContext();
  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const canEditAccommV2Bookable = !!user?.roles.some((role) => ACCOMM_V2_BOOKABLE_EDIT_ROLES.includes(role));
  const defaultValues = useMemo(() => getFormDefaults(propertyDetails), [propertyDetails]);
  const categoryOptions = useMemo(() => propertyCategories.allCategories, []);
  const countryOptions = useMemo(() => {
    const countries = getCountryNames().sort();
    if (propertyDetails.country && !countries.includes(propertyDetails.country)) {
      return [propertyDetails.country, ...countries];
    }
    return countries;
  }, [propertyDetails.country]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<PropertyMetadataFormData>({
    defaultValues,
  });

  // Reset form when propertyDetails changes
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const updatePropertyMutation = useMutation({
    mutationFn: async (data: PropertyMetadataFormData) => {
      return await patchProperty(
        { propertyId: propertyDetails.id },
        toPatchPayload(data, { includeAccommV2Bookable: canEditAccommV2Bookable }),
      );
    },
    onSuccess: (response) => {
      const updatedProperty = response.result;
      setPropertyDetails(updatedProperty);
      // The header and layouts render the page's own fetch result, not this context, so without
      // refetching they keep the pre-save values — including the capability chips.
      refreshPropertyDetails?.();
      queryClient.invalidateQueries({ queryKey: ['property', propertyDetails.id] });
      enqueueSnackbar('Property data updated successfully', { variant: 'success' });
      reset(getFormDefaults(updatedProperty));
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating property data: ${error.message}`, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    (data: PropertyMetadataFormData) => {
      updatePropertyMutation.mutate(data);
    },
    [updatePropertyMutation],
  );

  const copyPropertyId = useCallback(() => {
    navigator.clipboard.writeText(propertyDetails.id);
    enqueueSnackbar('Copied ID to clipboard', { variant: 'success', autoHideDuration: 3500 });
  }, [enqueueSnackbar, propertyDetails.id]);

  const accommV2BookableToggle = (
    <Controller
      name="accommV2Bookable"
      control={control}
      render={({ field: { value, onChange, ...field } }) => (
        <FormControlLabel
          control={
            <Switch
              {...field}
              checked={value}
              onChange={(_, checked) => onChange(checked)}
              disabled={!canEditAccommV2Bookable || updatePropertyMutation.isPending}
            />
          }
          label={ACCOMM_V2_BOOKABLE_LABEL}
        />
      )}
    />
  );

  if (variant === 'classic') {
    return (
      <Stack direction="column" gap={2}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: 'repeat(5, 1fr)' }}>
            <Box gridColumn={{ xs: '1', md: 'span 2' }}>
              <DefinitionTitle>Name</DefinitionTitle>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={updatePropertyMutation.isPending}
                  />
                )}
              />
            </Box>
            <Box>
              <DefinitionTitle>Chain Name</DefinitionTitle>
              <Controller
                name="chainName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={updatePropertyMutation.isPending}
                  />
                )}
              />
            </Box>
            <Box>
              <DefinitionTitle>Category</DefinitionTitle>
              <Controller
                name="category"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Autocomplete
                    {...field}
                    options={categoryOptions}
                    value={value}
                    onChange={(_, newValue) => onChange(newValue)}
                    renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
                    disabled={updatePropertyMutation.isPending}
                    fullWidth
                    openOnFocus
                    clearOnEscape
                  />
                )}
              />
            </Box>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="flex-start">
              {isDirty && (
                <Button type="submit" variant="contained" color="primary" disabled={updatePropertyMutation.isPending}>
                  {updatePropertyMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              )}
            </Box>

            <Box gridColumn={{ xs: '1', md: 'span 2' }}>
              <DefinitionTitle>ID</DefinitionTitle>
              <DefinitionText>{propertyDetails.id}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>Country</DefinitionTitle>
              <Controller
                name="country"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Autocomplete
                    {...field}
                    options={countryOptions}
                    value={value}
                    onChange={(_, newValue) => onChange(newValue)}
                    renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
                    disabled={updatePropertyMutation.isPending}
                    fullWidth
                    openOnFocus
                    clearOnEscape
                  />
                )}
              />
            </Box>
            <Box>
              <DefinitionTitle>City</DefinitionTitle>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={updatePropertyMutation.isPending}
                  />
                )}
              />
            </Box>
            <Box display="flex" alignItems="center">
              {accommV2BookableToggle}
            </Box>
            <Box>
              <DefinitionTitle>Timezone</DefinitionTitle>
              <DefinitionText>{propertyDetails.timezone || '-'}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>Airport</DefinitionTitle>
              <DefinitionText>{propertyDetails.airport || '-'}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>Logo ID</DefinitionTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <LogoThumbnail logoId={propertyDetails.logoId} />
                <DefinitionText>{propertyDetails.logoId || '-'}</DefinitionText>
              </Stack>
            </Box>
            <Box>
              <DefinitionTitle>Rating</DefinitionTitle>
              <DefinitionText>{propertyDetails.rating}</DefinitionText>
            </Box>
          </Box>
        </form>
      </Stack>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto' }}>
      <Accordion
        defaultExpanded
        sx={{
          border: 1,
          borderColor: 'divider',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon color="primary" />}
          sx={{
            bgcolor: 'primary.lightest',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Property data</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="column" gap={2.5}>
              <Box>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      variant="standard"
                      size="small"
                      fullWidth
                      disabled={updatePropertyMutation.isPending}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: 'text.secondary',
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <SidebarReadOnlyField label="ID" value={propertyDetails.id} onCopy={copyPropertyId} />
              <Controller
                name="chainName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chain Name"
                    variant="standard"
                    size="small"
                    fullWidth
                    disabled={updatePropertyMutation.isPending}
                  />
                )}
              />
              <Controller
                name="category"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Autocomplete
                    {...field}
                    options={categoryOptions}
                    value={value}
                    onChange={(_, newValue) => onChange(newValue)}
                    renderInput={(params) => <TextField {...params} label="Category" variant="standard" size="small" />}
                    disabled={updatePropertyMutation.isPending}
                    fullWidth
                    openOnFocus
                    clearOnEscape
                  />
                )}
              />
              <Controller
                name="country"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Autocomplete
                    {...field}
                    options={countryOptions}
                    value={value}
                    onChange={(_, newValue) => onChange(newValue)}
                    renderInput={(params) => <TextField {...params} label="Country" variant="standard" size="small" />}
                    disabled={updatePropertyMutation.isPending}
                    fullWidth
                    openOnFocus
                    clearOnEscape
                  />
                )}
              />
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="City"
                    variant="standard"
                    size="small"
                    fullWidth
                    disabled={updatePropertyMutation.isPending}
                  />
                )}
              />
              {accommV2BookableToggle}
              <SidebarReadOnlyField
                label="Rating"
                value={propertyDetails.rating ? String(propertyDetails.rating) : '-'}
              />
              <SidebarReadOnlyField label="Timezone" value={propertyDetails.timezone || '-'} />
              <SidebarReadOnlyField label="Airport" value={propertyDetails.airport || '-'} />
              <SidebarReadOnlyField
                label="Logo ID"
                value={propertyDetails.logoId || '-'}
                startAdornment={<LogoThumbnail logoId={propertyDetails.logoId} />}
              />
              <SidebarChipListField
                label="Property Types"
                values={(propertyDetails.propertyTypes ?? []).map((propertyType) => propertyType.value)}
              />
              <SidebarChipListField
                label="Holiday Types"
                values={(propertyDetails.holidayTypes ?? []).map((holidayType) => holidayType.value)}
              />

              <Box display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!isDirty || updatePropertyMutation.isPending}
                >
                  {updatePropertyMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
